import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

const q = (path) => async () => (await api.get(path)).data;

export const useProgramme = (slug) =>
  useQuery({ queryKey: ["programme", slug], queryFn: q(`/programmes/${slug}`), enabled: !!slug, retry: false });

export const useMyApplications = (enabled = true) =>
  useQuery({ queryKey: ["my-applications"], queryFn: q("/applications/me"), enabled });

export const useMyTrials = (enabled = true) =>
  useQuery({ queryKey: ["my-trials"], queryFn: q("/trials/me"), enabled });

export const useMyStories = (enabled = true) =>
  useQuery({ queryKey: ["my-stories"], queryFn: q("/stories/me"), enabled });

export const useMyConsents = (enabled = true) =>
  useQuery({ queryKey: ["my-consents"], queryFn: q("/me/consents"), enabled });

const MEMBER_KEYS = [["my-applications"], ["my-trials"], ["my-stories"], ["my-consents"], ["programme"]];

export function useMemberMutation(fn, onSuccess) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data, vars) => {
      MEMBER_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: k }));
      onSuccess?.(data, vars);
    },
  });
}

export const memberApi = {
  apply: (body) => api.post("/applications", body).then((r) => r.data),
  withdraw: (id) => api.post(`/applications/${id}/withdraw`).then((r) => r.data),
  accept: ({ id, shipping }) => api.post(`/applications/${id}/accept`, shipping).then((r) => r.data),
  decline: (id) => api.post(`/applications/${id}/decline`).then((r) => r.data),
  confirmDelivery: (trialId) => api.post(`/trials/${trialId}/confirm-delivery`).then((r) => r.data),
  completeTrial: (trialId) => api.post(`/trials/${trialId}/complete`).then((r) => r.data),
  saveCheckin: ({ trialId, day, body }) => api.put(`/trials/${trialId}/checkins/${day}`, body).then((r) => r.data),
  submitStory: (body) => api.post("/stories", body).then((r) => r.data),
  withdrawStory: (id) => api.delete(`/stories/${id}`).then((r) => r.data),
  updateConsents: (body) => api.put("/me/consents", body).then((r) => r.data),
  exportData: () => api.get("/me/export").then((r) => r.data),
  requestDeletion: () => api.post("/me/deletion-request").then((r) => r.data),
};

export const errorMessage = (e) => e?.response?.data?.detail || "Something went wrong. Please try again.";

export const APP_STATUS = {
  submitted: { label: "Submitted", tone: "sage", text: "We've received your application. We'll be in touch when it has been reviewed." },
  under_review: { label: "Under review", tone: "champagne", text: "Your application is being reviewed against the programme's eligibility and availability." },
  waitlisted: { label: "Waitlisted", tone: "champagne", text: "You're on the waitlist. If a place opens, we'll invite you." },
  selected: { label: "Invited", tone: "forest", text: "You've been selected. Accept your invitation and add shipping details to begin." },
  accepted: { label: "Accepted", tone: "forest", text: "Invitation accepted. Your trial is being prepared." },
  declined: { label: "Declined", tone: "muted", text: "You declined this invitation." },
  withdrawn: { label: "Withdrawn", tone: "muted", text: "You withdrew from this programme." },
  cancelled: { label: "Cancelled", tone: "muted", text: "This application was cancelled by ELA." },
  completed: { label: "Completed", tone: "sage", text: "Thank you — this experience is complete." },
};

export const TRIAL_STEPS = [
  { key: "awaiting_dispatch", label: "Preparing" },
  { key: "dispatched", label: "Dispatched" },
  { key: "in_progress", label: "In progress" },
  { key: "completed", label: "Completed" },
];
